import { estimateDeliveryDates } from '../../../apps/carrier-service/functions/utils/estimateDeliveryDates';


function deductDaysFromDate(date, days) {
  date.setDate(date.getDate() - days);
  return date;
}
/**
 * Get destination information from browser locale and fallback to GB if not available
 * @returns {Promise<Object>} Destination object with country, province, city, postal_code.
 */
async function getCountryCode() {
  // Try to get customer location from browser locale
  const locale = navigator.language;
  if (locale) {
    return locale?.split('-')[1];
  } else {
    return 'GB';
  }
}

/**
 * Calculate the soonest delivery date for a variant (works for both custom and non-custom vendors).
 * @param {Object} variant - The variant object with vendor and rate information.
 * @returns {Promise<Date|null>} The soonest delivery date or null if no eligible rates.
 */
async function calculateSoonestDeliveryDate(variant, countryCode) {
  const { vendor } = variant;
  const { eligibleRates } = vendor;

  if (!eligibleRates || eligibleRates.length === 0) {
    return null;
  }

  // Filter out rates with "INTERNAL" in the name as specified
  let filteredRates = eligibleRates.filter(rate => {
    const isInternal = rate?.displayName?.includes('INTERNAL') || rate?.internalName?.includes('[INTERNAL]');
    const eligibleForCountry = rate?.eligibleCountries?.includes(countryCode);
    return !isInternal && eligibleForCountry;
  });

  if (filteredRates.length === 0) {
    return null;
  }

  // Create Maps for estimateDeliveryDates function
  const shippingRates = new Map();
  const richItems = new Map();
  const vendors = new Map();
  const dateExclusionGroups = new Map();

  // Add the vendor to the Map first
  vendors.set(variant.vendor.internalName, {
    ...variant.vendor,
    dateExclusionGroupIds: vendor.dateExclusionGroups?.map(group => group.internalName) || []
  });

  // Additional check: If vendor has no eligible rates but we have filtered rates,
  // try to find at least one rate that could work
  const existingVendor = vendors.get(variant.vendor.internalName);
  if (existingVendor && (!existingVendor.eligibleRateIds || existingVendor.eligibleRateIds.length === 0)) {
    // Check if vendor has eligibleRates data (from the Liquid template)
    if (existingVendor.eligibleRates && Array.isArray(existingVendor.eligibleRates)) {
      // Extract rate IDs from the vendor's eligibleRates
      const vendorEligibleRateIds = existingVendor.eligibleRates.map(rate => rate.internalName);

      // Filter to only include rates that are in our filtered rates
      const validEligibleRateIds = vendorEligibleRateIds.filter(rateId =>
        filteredRates.some(rate => rate.internalName === rateId)
      );

      if (validEligibleRateIds.length > 0) {
        // Create a modified vendor object with the correct eligibleRateIds.
        const modifiedVendor = { 
          ...existingVendor, 
          eligibleRateIds: validEligibleRateIds,
          dateExclusionGroupIds: vendor.dateExclusionGroups?.map(group => group.internalName) || []
        };
        vendors.set(variant.vendor.internalName, modifiedVendor);
      } else {
        // Fallback to first filtered rate.
        const fallbackRate = filteredRates[0];
        if (fallbackRate) {
          const modifiedVendor = { 
            ...existingVendor, 
            eligibleRateIds: [fallbackRate.internalName],
            dateExclusionGroupIds: vendor.dateExclusionGroups?.map(group => group.internalName) || []
          };
          vendors.set(variant.vendor.internalName, modifiedVendor);
        }
      }
    } else {
      // Fallback to first filtered rate.
      const fallbackRate = filteredRates[0];
      if (fallbackRate) {
        const modifiedVendor = { 
          ...existingVendor, 
          eligibleRateIds: [fallbackRate.internalName],
          dateExclusionGroupIds: vendor.dateExclusionGroups?.map(group => group.internalName) || []
        };
        vendors.set(variant.vendor.internalName, modifiedVendor);
      }
    }
  }

  // Add shipping rates to the Map (only non-internal ones)
  for (const rate of filteredRates) {
    shippingRates.set(rate.internalName, {
      ...rate,
      dateExclusionGroupIds: rate.dateExclusionGroups?.map(group => group.internalName) || []
    });
  }

  // Convert preorderDate from string to Date object if it's a valid date
  let preorderDate = undefined;
  if (variant.preorderDate && variant.preorderDate.trim() !== '') {
    const date = new Date(variant.preorderDate);
    if (!isNaN(date.getTime())) {
      preorderDate = date;
    }
  }

  // Add the current variant as a rich item
  const richItem = {
    id: variant.id,
    title: variant.title,
    sku: variant.sku,
    quantity: 1, // Assume quantity 1 for PDP display
    price: variant.price,
    productId: variant.productId,
    leadDays: variant.leadDays || 0,
    preorderActive: variant.isPreorder || false,
    preorderDate: preorderDate,
    productTitle: variant.productTitle,
    vendorId: variant.vendor.internalName
  };
  richItems.set(variant.id, richItem);

  // Collect all date exclusion groups from the vendor
  if (vendor.dateExclusionGroups && Array.isArray(vendor.dateExclusionGroups)) {
    for (const group of vendor.dateExclusionGroups) {
      if (group && group.internalName) {
        dateExclusionGroups.set(group.internalName, group);
      }
    }
  }

  // Collect all date exclusion groups from all rates
  for (const rate of filteredRates) {
    if (rate.dateExclusionGroups && Array.isArray(rate.dateExclusionGroups)) {
      for (const group of rate.dateExclusionGroups) {
        if (group && group.internalName) {
          dateExclusionGroups.set(group.internalName, group);
        }
      }
    }
  }

  // Call estimateDeliveryDates with the prepared Maps.
  try {
    const estimatedDeliveryDates = estimateDeliveryDates({
      shippingRates,
      richItems,
      vendors,
      dateExclusionGroups,
      now: new Date()
    });

    // Extract all delivery dates and find the soonest one
    const deliveryDates = [];
    for (const [, rateDeliveryDates] of estimatedDeliveryDates) {
      for (const [, deliveryDate] of rateDeliveryDates) {
        if (deliveryDate && deliveryDate instanceof Date) {
          deliveryDates.push(deliveryDate);
        }
      }
    }

    // Return the soonest delivery date
    return deliveryDates.length > 0 ? deliveryDates.sort((a, b) => a.getTime() - b.getTime())[0] : null;

  } catch (error) {
    console.error('Error in estimateDeliveryDates:', error);
    return null;
  }
}

/**
 * Calculate total lead days for a variant
 * @param {Object} variant - The variant object
 * @param {string} countryCode - Country code for filtering rates
 * @returns {number} Total lead days
 */
function calculateTotalLeadDays(variant, countryCode) {
  const { vendor } = variant;
  const { eligibleRates } = vendor;

  // Get vendor lead days
  const vendorLeadDays = vendor.leadDays || 0;

  // Get SKU lead days
  const skuLeadDays = variant.leadDays || 0;

  // Get shipping rate lead days (use shortest)
  let shippingLeadDays = 0;
  if (eligibleRates && eligibleRates.length > 0) {
    const filteredRates = eligibleRates.filter(rate => {
      const isInternal = rate?.displayName?.includes('INTERNAL') || rate?.internalName?.includes('[INTERNAL]');
      const eligibleForCountry = rate?.eligibleCountries?.includes(countryCode);
      return !isInternal && eligibleForCountry;
    });

    if (filteredRates.length > 0) {
      shippingLeadDays = Math.min(...filteredRates.map(rate => rate.leadDays || 0));
    }
  }

  // Return total
  return vendorLeadDays + skuLeadDays + shippingLeadDays;
}

/**
 * Create an automated link block with optional modal.
 * @param {Object} options - Configuration options.
 * @param {string} options.linkTitle - The title/text of the link.
 * @param {string} options.linkUrl - The URL the link points to.
 * @param {string} options.linkTarget - The target attribute for the link.
 * @param {Object} options.modalPage - Object containing modal title and content.
 * @param {string} options.modalId - Unique ID for the modal.
 * @param {boolean} options.isFirstFallback - Whether this is the first fallback block.
 * @returns {Object} Object containing the container and modal elements.
 */
function createAutomatedLinkBlock({
  linkTitle,
  linkUrl,
  linkTarget,
  modalPage,
  modalId,
  isFirstFallback = false
}) {
  const container = document.createElement('div');
  container.className = `product-link-block${isFirstFallback ? ' product-link-block--first-fallback' : ''}`;

  const linkDiv = document.createElement('div');
  linkDiv.className = 'product-link-block__link';

  const link = document.createElement('a');
  link.className = `body-sm font-medium product-link-block__text${linkUrl ? ' product-link-block__link--true' : ''}`;
  if (linkUrl) {
    link.href = linkUrl;
    link.target = linkTarget;
  }
  link.textContent = linkTitle;
  linkDiv.appendChild(link);

  if (modalPage && modalPage.title) {
    const modalOpener = document.createElement('modal-opener');
    modalOpener.className = 'product-link-modal__opener';
    modalOpener.setAttribute('data-modal', `#ProductLinkModal-${modalId}`);

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'product-link-block__icon-button';
    button.setAttribute('aria-haspopup', 'dialog');
    button.setAttribute('aria-label', 'Open information modal');

    const iconSpan = document.createElement('span');
    iconSpan.className = 'product-link-block__icon';
    const svgWrapper = document.createElement('span');
    svgWrapper.className = 'svg-wrapper svg-wrapper--small';
    svgWrapper.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M.833 8a7.167 7.167 0 1 1 14.334 0A7.167 7.167 0 0 1 .833 8ZM8.13 6.687a.98.98 0 0 1 .6.305c.18.2.233.429.253.591C9 7.725 9 7.891 9 8.04V11a.667.667 0 1 1-1.333 0V8a.667.667 0 1 1 0-1.333h.027c.132 0 .295 0 .436.02ZM8 4.667A.667.667 0 1 0 8 6a.667.667 0 0 0 0-1.333Z" fill="currentColor"/></svg>`;

    iconSpan.appendChild(svgWrapper);
    button.appendChild(iconSpan);
    modalOpener.appendChild(button);
    linkDiv.appendChild(modalOpener);
  }

  container.appendChild(linkDiv);

  // Create modal dialog if modal content exists.
  let modalDialog = null;
  if (modalPage && modalPage.title) {
    // Escape HTML in title to prevent XSS.
    const escapeHtml = (text) => {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    };

    // Build the complete modal HTML as a template literal and parse it.
    // This ensures the modal-dialog element is created with its content already inside,
    // allowing the ModalDialog constructor to find the close button.
    const modalHTML = `
      <modal-dialog id="ProductLinkModal-${modalId}" class="product-popup-modal">
        <div role="dialog" aria-label="Product Information" aria-modal="true" class="product-popup-modal__content" tabindex="0">
          <div class="product-popup-modal__wrapper">
            <div class="product-popup-modal__header">
              <h2 class="product-popup-modal__header-title heading-2xl">${escapeHtml(modalPage.title)}</h2>
              <button id="ModalClose-${modalId}" type="button" class="product-popup-modal__toggle" aria-label="Close">
                <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-close" viewBox="0 0 24 24" fill="none">
                  <path d="M5.35404 4.64645C5.15878 4.45118 4.8422 4.45118 4.64694 4.64645C4.45167 4.84171 4.45167 5.15829 4.64693 5.35355L11.2934 12L4.64693 18.6465C4.45167 18.8417 4.45167 19.1583 4.64694 19.3536C4.8422 19.5488 5.15878 19.5488 5.35404 19.3536L12.0005 12.7071L18.6469 19.3536C18.8422 19.5488 19.1588 19.5488 19.3541 19.3536C19.5493 19.1583 19.5493 18.8417 19.3541 18.6465L12.7076 12L19.3541 5.35355C19.5493 5.15829 19.5493 4.84171 19.3541 4.64645C19.1588 4.45118 18.8422 4.45118 18.6469 4.64645L12.0005 11.2929L5.35404 4.64645Z" fill="currentColor"></path>
                </svg>
              </button>
            </div>
            <div class="product-popup-modal__container">
              <div class="product-popup-modal__content-info rte">
                ${modalPage.content || ''}
              </div>
            </div>
          </div>
        </div>
      </modal-dialog>
    `;

    // Parse HTML string into DOM element using DOMParser.
    const parser = new DOMParser();
    const doc = parser.parseFromString(modalHTML, 'text/html');
    modalDialog = doc.body.firstElementChild;
  }

  return { container, modal: modalDialog };
}

async function generateAutomatedLinkBlocks(blockData, variant, blockId) {
  const blocksToRender = [];

  if(!variant.vendor.internalName) {                                            // VARIANT HAS NO VENDOR
    const blockResult = createAutomatedLinkBlock({
      linkTitle: '⚠️ Variant has no vendor!',
      linkUrl: '',
      linkTarget: '',
      modalPage: '',
      modalId: '',
      isFirstFallback: false
    });
    blocksToRender.push(blockResult);
    return blocksToRender;
  }

  if(variant.available === false) {                                              // VARIANT IS NOT AVAILABLE
    if(blockData.settings.out_of_stock_message){
      const blockResult = createAutomatedLinkBlock({
        linkTitle: blockData.settings.out_of_stock_message,
        linkUrl: '',
        linkTarget: '',
        modalPage: '',
        modalId: '',
        isFirstFallback: false
      });
      blocksToRender.push(blockResult);
    }
  } else {                                                                      // VARIANT IS AVAILABLE
    // Show preorder date if it's in the future
    if(variant.isPreorder && variant.preorderDate) {                            // VARIANT IS A PREORDER
      const dispatchDate = new Date(variant.preorderDate);
      const earliestDate = new Date(dispatchDate);
      if(earliestDate > new Date()) {
        const formattedEarliestDate = formatDeliveryDate(earliestDate, { addDayName: true, addSuffix: true, hideYearIfCurrent: true });
        const blockResult = createAutomatedLinkBlock({
          linkTitle: `${blockData.settings.preorder_message || ''} ${formattedEarliestDate}`,
          linkUrl: '',
          linkTarget: '',
          modalPage: '',
          modalId: '',
          isFirstFallback: false
        });
        blocksToRender.push(blockResult);
      }
    } else {
      // Calculate delivery date for custom vendors
      if (variant.vendor.isCustom) {                                             // VARIANT HAS A CUSTOM VENDOR
        try {
          const countryCode = await getCountryCode();

          // Check if we should show shipping details block
          if (blockData.settings.show_shipping_details_block) {
            if (blockData.settings.custom_show_lead_days_instead_of_calculation) {
              // Show total lead days instead of date calculation
              const totalLeadDays = calculateTotalLeadDays(variant, countryCode);
              const prefix = blockData.settings.custom_shipping_details_prefix || '';
              const suffix = blockData.settings.custom_shipping_details_suffix || '';

              const linkTitle = `${prefix} ${totalLeadDays} ${suffix}`;

              const blockResult = createAutomatedLinkBlock({
                linkTitle,
                linkUrl: '',
                linkTarget: '',
                modalPage: blockData.pageContents?.personalisedLeadTimeModal,
                modalId: `custom-shipping-${blockId}`,
                isFirstFallback: false
              });
              blocksToRender.push(blockResult);
            } else {
              // Show date calculation
              const deliveryDate = await calculateSoonestDeliveryDate(variant, countryCode);

              if (deliveryDate) {
                // Since we're in the custom vendor block, use custom settings
                const displayDateCalculation = blockData.settings.custom_display_date_calculation;

                if (displayDateCalculation) {
                  const earliestDate = new Date(deliveryDate);
                  const deductedDate = deductDaysFromDate(new Date(deliveryDate), blockData.deliveryDateRange);
                  const formattedEarliestDate = formatDeliveryDate(earliestDate, { addDayName: true, addSuffix: true, hideYearIfCurrent: true });
                  const formattedDeductedDate = formatDeliveryDate(deductedDate, { addDayName: true, addSuffix: true, hideYearIfCurrent: true });

                  const prefix = blockData.settings.custom_shipping_details_prefix || '';
                  const suffix = blockData.settings.custom_shipping_details_suffix || '';

                  const linkTitle = `${prefix} ${formattedDeductedDate} - ${formattedEarliestDate}${suffix ? ' ' + suffix : ''}`;

                  const blockResult = createAutomatedLinkBlock({
                    linkTitle: linkTitle,
                    linkUrl: '',
                    linkTarget: '',
                    modalPage: blockData.pageContents?.personalisedLeadTimeModal,
                    modalId: `custom-shipping-${blockId}`,
                    isFirstFallback: false
                  });
                  blocksToRender.push(blockResult);
                } else {
                  const blockResult = createAutomatedLinkBlock({
                    linkTitle: blockData.settings.custom_shipping_details_message || '',
                    linkUrl: '',
                    linkTarget: '',
                    modalPage: blockData.pageContents?.personalisedLeadTimeModal,
                    modalId: `custom-shipping-${blockId}`,
                    isFirstFallback: false
                  });
                  blocksToRender.push(blockResult);
                }
              } else {
                console.warn('PDP Link Block JS: Delivery date unavailable');
              }
            }
          } else {
            // Fallback to simple ready to ship message if no shipping details block
            const blockResult = createAutomatedLinkBlock({
              linkTitle: 'Ready to ship',
              linkUrl: '',
              linkTarget: '',
              modalPage: '',
              modalId: '',
              isFirstFallback: false
            });
            blocksToRender.push(blockResult);
          }
        } catch (error) {
          console.error('Error calculating delivery date:', error);
        }
      } else {                                                               // VARIANT HAS A NON-CUSTOM VENDOR
        // Check if shipping details block should be shown for non-custom vendors
        if (blockData.settings.show_shipping_details_block) {
          try {
            const countryCode = await getCountryCode();

            if (blockData.settings.non_custom_show_lead_days_instead_of_calculation) {
              // Show total lead days instead of date calculation
              const totalLeadDays = calculateTotalLeadDays(variant, countryCode);
              const prefix = blockData.settings.non_custom_shipping_details_prefix || '';
              const suffix = blockData.settings.non_custom_shipping_details_suffix || '';

              const blockResult = createAutomatedLinkBlock({
                linkTitle: `${prefix} ${totalLeadDays} ${suffix}`,
                linkUrl: '',
                linkTarget: '',
                modalPage: blockData.pageContents?.nonPersonalisedLeadTimeModal,
                modalId: `non-custom-shipping-${blockId}`,
                isFirstFallback: false
              });
              blocksToRender.push(blockResult);
            } else {
              const displayDateCalculation = blockData.settings.non_custom_display_date_calculation;
              if (displayDateCalculation) {
                // For non-custom vendors, calculate and show single delivery date
                const deliveryDate = await calculateSoonestDeliveryDate(variant, countryCode);

                if (deliveryDate) {
                  const formattedDate = formatDeliveryDate(deliveryDate, { addDayName: true, addSuffix: true, hideYearIfCurrent: true });
                  const prefix = blockData.settings.non_custom_shipping_details_prefix || '';

                  const blockResult = createAutomatedLinkBlock({
                    linkTitle: `${prefix} ${formattedDate}`,
                    linkUrl: '',
                    linkTarget: '',
                    modalPage: blockData.pageContents?.nonPersonalisedLeadTimeModal,
                    modalId: `non-custom-shipping-${blockId}`,
                    isFirstFallback: false
                  });
                  blocksToRender.push(blockResult);
                } else {
                  console.warn('Non-custom vendor: Delivery date unavailable');
                  // Fallback to simple message if calculation fails
                  const blockResult = createAutomatedLinkBlock({
                    linkTitle: blockData.settings.non_custom_shipping_details_message || '',
                    linkUrl: '',
                    linkTarget: '',
                    modalPage: blockData.pageContents?.nonPersonalisedLeadTimeModal,
                    modalId: `non-custom-shipping-${blockId}`,
                    isFirstFallback: false
                  });
                  blocksToRender.push(blockResult);
                }
              } else {
                // Show simple message when date calculation is disabled
                const blockResult = createAutomatedLinkBlock({
                  linkTitle: blockData.settings.non_custom_shipping_details_message || '',
                  linkUrl: '',
                  linkTarget: '',
                  modalPage: blockData.settings.non_custom_shipping_details_page,
                  modalId: `non-custom-shipping-${blockId}`,
                  isFirstFallback: false
                });
                blocksToRender.push(blockResult);
              }
            }
          } catch (error) {
            console.error('Error calculating delivery date for non-custom vendor:', error);
            // Fallback to simple message on error
            const blockResult = createAutomatedLinkBlock({
              linkTitle: blockData.settings.non_custom_shipping_details_message || '',
              linkUrl: '',
              linkTarget: '',
              modalPage: blockData.settings.non_custom_shipping_details_page,
              modalId: `non-custom-shipping-${blockId}`,
              isFirstFallback: false
            });
            blocksToRender.push(blockResult);
          }
        }
      }
    }

    // Handle personalised sibling blocks if enabled
    if (blockData.settings.show_personalised_sibling_blocks) {
      // Non-personalised sibling block
      if (blockData.settings.non_personalised_sibling_message && blockData.siblings.nonPersonalised?.id) {
        const siblingUrl = `/products/${blockData.siblings.nonPersonalised.handle}` || '';
        // Replace placeholders in message
        let message = blockData.settings.non_personalised_sibling_message;
        if (blockData.siblings.nonPersonalised.price) {
          const currency = window.defaultTagalysCurrencyLabel || '£';
          const price = (blockData.siblings.nonPersonalised.price / 100).toFixed(2);
          message = message.replace('{siblingPrice}', `${currency}${price}`);
        }
        if (blockData.siblings.nonPersonalised.type) {
          message = message.replace('{siblingType}', blockData.siblings.nonPersonalised.type.toLowerCase());
          message = message.replace('{_siblingType}', blockData.siblings.nonPersonalised.type);
        }

        const blockResult = createAutomatedLinkBlock({
          linkTitle: message,
          linkUrl: siblingUrl,
          linkTarget: '_blank',
          modalPage: blockData.pageContents?.nonPersonalisedSibling,
          modalId: `non-personalised-sibling-${blockId}`,
          isFirstFallback: false
        });
        blocksToRender.push(blockResult);
      }

      // Personalised sibling block
      if (blockData.settings.personalised_sibling_message && blockData.siblings.personalised?.id) {
        const siblingUrl = `/products/${blockData.siblings.personalised.handle}` || '';
        // Replace placeholders in message
        let message = blockData.settings.personalised_sibling_message;
        if (blockData.siblings.personalised.price) {
          const currency = window.defaultTagalysCurrencyLabel || '£';
          const price = (blockData.siblings.personalised.price / 100).toFixed(2);
          message = message.replace('{siblingPrice}', `${currency}${price}`);
        }
        if (blockData.siblings.personalised.type) {
          message = message.replace('{siblingType}', blockData.siblings.personalised.type.toLowerCase());
          message = message.replace('{_siblingType}', blockData.siblings.personalised.type);
        }

        const blockResult = createAutomatedLinkBlock({
          linkTitle: message,
          linkUrl: siblingUrl,
          linkTarget: '_blank',
          modalPage: blockData.pageContents?.personalisedSibling,
          modalId: `personalised-sibling-${blockId}`,
          isFirstFallback: false
        });
        blocksToRender.push(blockResult);
      }
    }

  }

  return blocksToRender;
}

/**
 * Format a date for display in the UI.
 * @param {Date} date - The date to format.
 * @param {Object} options - Formatting options
 * @param {boolean} options.addDayName - Whether to add short day name at start
 * @param {boolean} options.addSuffix - Whether to add nominal suffix (st, nd, rd, th)
 * @param {boolean} options.hideYearIfCurrent - Whether to hide year if it's current year
 * @returns {string} Formatted date string.
 */
function formatDeliveryDate(date, options = { addDayName: false, addSuffix: false, hideYearIfCurrent: false }) {
  const { addDayName, addSuffix, hideYearIfCurrent } = options;

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const dayName = addDayName ? `${dayNames[date.getDay()]} ` : '';

  const day = date.getDate();
  let suffix = '';
  if (addSuffix) {
    if (day >= 11 && day <= 13) {
      suffix = 'th';
    } else {
      switch (day % 10) {
        case 1: suffix = 'st'; break;
        case 2: suffix = 'nd'; break;
        case 3: suffix = 'rd'; break;
        default: suffix = 'th';
      }
    }
  }

  const currentYear = new Date().getFullYear();
  const showYear = !hideYearIfCurrent || date.getFullYear() !== currentYear;

  const locale = navigator.language || 'en-GB';
  const dateFormatter = new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: showYear ? 'numeric' : undefined
  });

  const formatted = dateFormatter.format(date);
  return `${dayName}${day}${suffix} ${formatted}`;
}

/**
 * Render automated link blocks for a specific variant.
 * @param {Object} options - Render options.
 * @param {string} options.blockId - The block ID to render.
 * @param {number} options.selectedVariantId - The selected variant ID.
 * @returns {Promise<void>}
 */
async function renderAutomatedLinkBlocks({ blockId, selectedVariantId }) {
  const blockData = window?.automatedLinkBlocks?.[`${blockId}`];
  const container = document.getElementById(`automated-link-blocks`);
  const variant = blockData?.variants?.find(variant => String(variant.id) === String(selectedVariantId));

  if (!blockData) {
    console.log(`🔗 Automated Link Block JS: Block data not found for blockId: ${blockId}`);
    return;
  }
  if (!container) {
    console.log(`🔗 Automated Link Block JS: Container not found for blockId: ${blockId}`);
    return;
  }
  if (!variant) {
    console.log(`🔗 Automated Link Block JS: Variant not found for blockId: ${blockId} and selectedVariantId: ${selectedVariantId}`);
    return;
  }

  const blockResults = await generateAutomatedLinkBlocks(blockData, variant, blockId);

  // Clear the container.
  container.innerHTML = '';

  // Remove any existing modals from this block to prevent duplicates.
  const existingModals = document.querySelectorAll(`[id^="ProductLinkModal-"]`);
  existingModals.forEach(modal => {
    // Only remove modals that belong to this block.
    const modalId = modal.id.replace('ProductLinkModal-', '');
    if (modalId.includes(blockId)) {
      modal.remove();
    }
  });

  // Append the containers (which contain modal-openers) to the main container.
  blockResults.forEach(blockResult => {
    container.appendChild(blockResult.container);
  });

  // Append all modals to the body - the modal-dialog custom element will handle initialization automatically.
  blockResults.forEach(blockResult => {
    if (blockResult.modal) {
      document.body.appendChild(blockResult.modal);
    }
  });

  // Wait for custom elements to be fully initialized before setting up event listeners.
  requestAnimationFrame(() => {
    // Manually initialize modal-openers since their constructors ran before buttons were added.
    container.querySelectorAll('modal-opener').forEach(modalOpener => {
      const button = modalOpener.querySelector('button');
      if (button && !button._modalInitialized) {
        button._modalInitialized = true;
        button.addEventListener('click', () => {
          const modal = document.querySelector(modalOpener.getAttribute('data-modal'));
          if (modal && typeof modal.show === 'function') {
            modal.show(button);
          } else {
            console.error('Modal not ready or show method not available:', modalOpener.getAttribute('data-modal'));
          }
        });
      }
    });
  });
}

window.renderAutomatedLinkBlocks = renderAutomatedLinkBlocks;